// Third-party
import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { MessageType } from '@/types/messageType';

// Components
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';

interface ReplyFooterButtonsProps {
    className?: string;
    comment: MessageType;
    countReplies: number;
    isLoadingInitialData: boolean;
    isLoadingMore: boolean;
    isLoadMore: boolean;
    isThreadOpen: boolean;
    loadMoreReplies: () => void;
    toggleThread: () => void;
}

export function ReplyFooterButtons({
    className,
    comment,
    countReplies,
    isLoadingInitialData,
    isLoadingMore,
    isLoadMore,
    isThreadOpen,
    loadMoreReplies,
    toggleThread
}: Readonly<ReplyFooterButtonsProps>) {
    const { t } = useTranslation('commons');

    const showLoadMoreButton =
        (isLoadMore && isThreadOpen) || (isLoadMore && !isThreadOpen && countReplies < 3);
    const showThreadToggleButton = countReplies > 2;
    const isLoading = isLoadingMore || isLoadingInitialData;
    if (!isLoadMore && countReplies < 2) {
        return null;
    }

    return (
        <div
            className={clsx(
                'flex space-x-5',
                { 'mt-1.5': countReplies < 3, 'mt-0.5': countReplies > 2 },
                className
            )}>
            {showLoadMoreButton && (
                <button
                    className={clsx('flex items-center text-xs text-icoBlue', {
                        'opacity-50': isLoading
                    })}
                    disabled={isLoading}
                    onClick={loadMoreReplies}>
                    {isLoading && <Loader className="mr-1 inline-block h-3 w-3 text-icoBlue" />}

                    {t('comment.nb_more_comments', {
                        count: comment?.replies_count - countReplies
                    })}

                    <Icon icon="chevron-bottom" className="relative top-px ml-1" />
                </button>
            )}

            {showThreadToggleButton && (
                <button
                    className={clsx('text-xs', {
                        'text-icoGray-500': isThreadOpen,
                        'text-icoBlue': !isThreadOpen
                    })}
                    onClick={toggleThread}>
                    {!isThreadOpen
                        ? t('comment.nb_more_comments', {
                              count: comment?.replies_count - 2
                          })
                        : t('comment.close_thread')}

                    <Icon
                        icon={isThreadOpen ? 'chevron-top' : 'chevron-bottom'}
                        className="relative top-px ml-1"
                    />
                </button>
            )}
        </div>
    );
}
